import { useCallback, useEffect, useRef, useState } from "react";
import useMasterAdminFormHeader from "../masterAdmin/useMasterAdminFormHeader";
import NavBar from "../../component/navBar/NavBar";
import Title from "../../component/title/Title";
import Table from "../../component/table/Table";
import DeleteModal from "../../component/modal/DeleteModal";
import SnackBar from "../../component/Snackbar/Snackbar";
import "./FamilyManagement.css";
import {
  PrimaryButton,
  SecondaryButton,
} from "../../component/buttonComponent/ButtonComponent";
import FamilyModal from "../../component/modal/FamilyModal";
import BulkImportModal from "../../component/modal/BulkImportModal";
import UpdateEmailStatusModal from "../../component/modal/UpdateEmailStatusModal";
import TextInput from "../../component/textInput/TextInput";
import Dropdown from "../../component/dropdown/Dropdown";
import DatePicker from "../../component/datePicker/DatePicker";
import {
  formatCombineFamilyPhone,
  formatParseFamilyPhone,
} from "../../config/utilities/formatPhone";
import { formatDate } from "../../config/utilities/fDate";
import { useAuth } from "../../config/utilities/useAuth";
import { useTimezone } from "../../config/utilities/useTimezone";
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import { parseISO } from "date-fns";
import {
  APIDeleteFamily,
  APIGetFamilies,
  APIUpdateFamily,
  APIUpdateFamilySubscription,
  APIUpdateParentStatus,
  APIBulkImportFamilies,
  APIUpdateEmailStatus,
} from "../../config/utilities/APIs";
import { RequiredLabel } from "../../component/requiredLabel/RequiredLabel";
import { downloadFailedFamiliesFile } from "../../config/utilities/csvUtils";
import { ENABLE_FAMILY_ACCOUNT_TYPES } from "../../config/featureFlags";

const headers = [
  { key: "rowNumber", name: "No." },
  ...(ENABLE_FAMILY_ACCOUNT_TYPES ? [{ key: "accountType", name: "Type" }] : []),
  { key: "parent", name: "Parents" },
  { key: "child", name: "Children" },
  { key: "address", name: "Address" },
];

const parentHeaders = [
  { key: "fullName", name: "Full Name", sortable: true },
  { key: "email", name: "Email", sortable: true },
  { key: "phoneNumber", name: "Phone", sortable: true },
  { key: "status", name: "Status", sortable: true },
  { key: "emailOnboardStatus", name: "Onboarding Email", sortable: true },
];

const childHeaders = [
  { key: "fullName", name: "Full Name", sortable: true },
  { key: "preferredName", name: "Preferred Name", sortable: true },
  { key: "class", name: "Class", sortable: true },
  { key: "stopName", name: "Stops", sortable: true },
];

const subscriptionPlanOptions = [
  { label: "Trial", value: "TRIAL" },
  { label: "Standard", value: "STANDARD" },
  { label: "Premium", value: "PREMIUM" },
  { label: "Restricted", value: "RESTRICTED" },
];
const subscriptionFieldWidth = "10rem";

const getTrialMaxDate = (timezone) => {
  const now = new Date();
  const fourWeeksFromNow = new Date(now);
  fourWeeksFromNow.setDate(now.getDate() + 28);
  return formatInTimeZone(fourWeeksFromNow, timezone, "yyyy-MM-dd");
};

const disableToday = (date, timezone) => {
  if (!date) return false;
  const todayInTimezone = formatInTimeZone(new Date(), timezone, "yyyy-MM-dd");
  if (typeof date.format === "function") {
    return date.format("YYYY-MM-DD") === todayInTimezone;
  }
  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return false;
  return formatInTimeZone(parsedDate, timezone, "yyyy-MM-dd") === todayInTimezone;
};

const normalizeSubscriptionPlanFromRow = (subscription, selectedRow) => {
  const fromPlan = String(subscription?.plan || "").trim().toUpperCase();
  if (["TRIAL", "STANDARD", "PREMIUM", "RESTRICTED"].includes(fromPlan)) {
    return fromPlan;
  }
  const fromAccountType = String(
    selectedRow?.accountType ||
      subscription?.accountType ||
      ""
  )
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "");
  if (
    ["TRIAL", "STANDARD", "PREMIUM", "RESTRICTED"].includes(fromAccountType)
  ) {
    return fromAccountType;
  }
  return "";
};

export default function FamilyManagement({
  institutionIdOverride,
  masterAdminMode = false,
}) {
  const [action, setAction] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarType, setSnackbarType] = useState("");
  const [snackbarTitle, setSnackbarTitle] = useState("");
  const [snackbarSubtitle, setSnackbarSubtitle] = useState("");

  const [showAddModal, setShowAddModal] = useState(false);
  const [addModalType, setAddModalType] = useState("");

  const [parentId, setParentId] = useState(1);
  const [childId, setChildId] = useState(1);

  const [selectedParentData, setSelectedParentData] = useState(null);
  const [selectedChildData, setSelectedChildData] = useState(null);

  const [loading, setLoading] = useState(false);
  const [initialEditInput, setInitialEditInput] = useState(null);
  const [keyUp, setKeyUp] = useState("", false);

  const [fetchedFamilyData, setFetchedFamilyData] = useState([]);

  const [onlyView, setOnlyView] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [showBulkImportProgress, setShowBulkImportProgress] = useState(false);
  const [bulkImportProgressTitle, setBulkImportProgressTitle] = useState("");
  const [bulkImportProgressSubtitle, setBulkImportProgressSubtitle] = useState("");
  const [failedFamiliesFile, setFailedFamiliesFile] = useState(null);
  const [showUpdateEmailStatusModal, setShowUpdateEmailStatusModal] = useState(false);

  const { privilege, token } = useAuth();
  const { timezone } = useTimezone();
  const role = privilege?.role;
  const institutionId = institutionIdOverride ?? privilege?.institutionId;

  const [input, setInput] = useState({
    address: "",
    subscription: {
      plan: "",
      validUntil: "",
      gracePeriodDays: "7",
      startAt: "",
    },
    parent: [],
    child: [],
  });

  const [error, setError] = useState({
    firstName: false,
    lastName: false,
    email: false,
    phoneNumber: false,
    dob: false,
    grade: false,
    fullName: false,
  });
  const [gracePeriodError, setGracePeriodError] = useState(false);

  const fetchFamilies = async (page = 1, searchTerm = "") => {
    setLoading(true);
    try {
      const response = await APIGetFamilies(
        setLoading,
        page,
        searchTerm,
        institutionId
      );

      if (response.status === 200) {
        const { families, pagination } = response.data.data;

        const numberedFamilies = families.map((family, index) => ({
          ...family,
          rowNumber:
            (pagination.currentPage - 1) * pagination.totalPerPage + index + 1,
        }));

        setFetchedFamilyData({ families: numberedFamilies, pagination });
        setPageNumber(page);
      } else if (response.status === 400) {
        console.warn("Bad Request:", response);
      } else if (response.status === 401) {
        console.warn("Unauthorized:", response);
      } else if (response.status === 403) {
        console.warn("Forbidden:", response);
      } else if (response.status === 404) {
        console.warn("Not Found:", response);
      } else if (response.status >= 500) {
        console.error("Server Error:", response);
      } else {
        console.warn("Unexpected Status Code:", response.status, response);
      }
    } catch (error) {
      console.error("Error fetching families:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!institutionId) return;

    fetchFamilies();
    
    // Restore bulk import progress state from sessionStorage
    const savedBulkImportState = sessionStorage.getItem('bulkImportProgress');
    if (savedBulkImportState) {
      try {
        const state = JSON.parse(savedBulkImportState);
        if (state.showProgress && state.loading) {
          // Only restore if import is still in progress
          setShowBulkImportProgress(true);
          setBulkImportProgressTitle(state.title || "Bulk Import in Progress");
          setBulkImportProgressSubtitle(state.subtitle || "Please wait while we import your families...");
          setLoading(true);
        } else if (state.showProgress && !state.loading) {
          // Import completed, show final result briefly then clear
          setShowBulkImportProgress(true);
          setBulkImportProgressTitle(state.title || "Bulk Import Completed");
          setBulkImportProgressSubtitle(state.subtitle || "");
          setLoading(false);
          // Auto-close after 5 seconds since it already completed
          setTimeout(() => {
            setShowBulkImportProgress(false);
            sessionStorage.removeItem('bulkImportProgress');
          }, 5000);
        }
      } catch (error) {
        console.error("Error restoring bulk import state:", error);
        sessionStorage.removeItem('bulkImportProgress');
      }
    }
  }, [institutionId]);

  useEffect(() => {
    if (!institutionId) return;
    fetchFamilies(1, searchTerm);
  }, [searchTerm, institutionId]);

  // Persist bulk import progress state to sessionStorage
  useEffect(() => {
    if (showBulkImportProgress) {
      sessionStorage.setItem('bulkImportProgress', JSON.stringify({
        showProgress: true,
        title: bulkImportProgressTitle,
        subtitle: bulkImportProgressSubtitle,
        loading: loading,
      }));
    } else {
      sessionStorage.removeItem('bulkImportProgress');
    }
  }, [showBulkImportProgress, bulkImportProgressTitle, bulkImportProgressSubtitle, loading]);

  const handleResetRef = useRef(async () => {});

  const getFamilyFormViewConfig = useCallback(
    (currentAction) => {
      switch (currentAction) {
        case "edit":
          return {
            title: onlyView ? "View Family" : "Edit Family",
            subtitle: onlyView
              ? "View family details for this workspace"
              : "Update family details for this workspace",
            backLabel: "Back to Family Management",
          };
        case "create":
          return {
            title: "Create Family",
            subtitle: "Add a new family to this workspace",
            backLabel: "Back to Family Management",
          };
        default:
          return null;
      }
    },
    [onlyView]
  );

  const clearFormHeader = useMasterAdminFormHeader({
    enabled: masterAdminMode,
    action: action === "edit" || action === "create" ? action : null,
    revision: action === "edit" ? onlyView : undefined,
    getViewConfig: getFamilyFormViewConfig,
    onBack: () => handleResetRef.current(),
  });

  const handleReset = async () => {
    if (masterAdminMode) {
      clearFormHeader();
    }

    setInput({
      address: "",
      subscription: {
        plan: "",
        validUntil: "",
        gracePeriodDays: "7",
        startAt: "",
      },
      parent: [],
      child: [],
    });

    setError({
      address: false,
      parent: false,
      child: false,
    });
    setGracePeriodError(false);

    setAction(null);
    setSelectedRowData(null);
    setShowAddModal(false);
    setAddModalType("");
    setParentId(1);
    setChildId(1);
    setSelectedParentData(null);
    setSelectedChildData(null);
    setOnlyView(false);
    setPageNumber(1);
    setShowBulkImportModal(false);
    await fetchFamilies(1, searchTerm);
  };

  handleResetRef.current = handleReset;

  const handleBulkImport = async (csvFile, sendEmail = true) => {
    // Show progress snackbar immediately
    setShowBulkImportProgress(true);
    setBulkImportProgressTitle("Bulk Import in Progress");
    setBulkImportProgressSubtitle("Please wait while we import your families...");
    
    // Close modal immediately so user can continue working
    setShowBulkImportModal(false);
    
    setLoading(true);
    try {
      const response = await APIBulkImportFamilies(
        csvFile,
        setLoading,
        institutionId,
        token,
        sendEmail
      );

      if (response.status === 200) {
        const data = response.data?.data || {};
        const totalProcessed = data.totalProcessed || 0;
        const successfulCount = data.successful || 0;
        const failedCount = data.failed || 0;
        const skippedCount = data.skipped || 0;

        // Store failed families file if available
        if (data.failedFamiliesFile) {
          setFailedFamiliesFile(data.failedFamiliesFile);
        } else {
          setFailedFamiliesFile(null);
        }

        // Update progress snackbar with results
        if (failedCount === 0 && skippedCount === 0) {
          // All successful, no failures or skips
          setBulkImportProgressTitle("Bulk Import Successful");
          setBulkImportProgressSubtitle(`${successfulCount} out of ${totalProcessed} ${totalProcessed === 1 ? "family" : "families"} inserted successfully.`);
        } else if (failedCount === 0 && skippedCount > 0) {
          // Some successful, some skipped (duplicates), no failures
          if (successfulCount === 0) {
            setBulkImportProgressTitle("Bulk Import Completed");
            setBulkImportProgressSubtitle(`0 out of ${totalProcessed} ${totalProcessed === 1 ? "family" : "families"} inserted. ${skippedCount} ${skippedCount === 1 ? "family" : "families"} already exist.`);
          } else {
            setBulkImportProgressTitle("Bulk Import Completed");
            setBulkImportProgressSubtitle(`${successfulCount} out of ${totalProcessed} ${totalProcessed === 1 ? "family" : "families"} inserted. ${skippedCount} ${skippedCount === 1 ? "family" : "families"} already exist.`);
          }
        } else if (successfulCount === 0 && skippedCount === 0) {
          // All failed
          setBulkImportProgressTitle("Bulk Import Failed");
          setBulkImportProgressSubtitle(`0 out of ${totalProcessed} ${totalProcessed === 1 ? "family" : "families"} inserted. ${failedCount} ${failedCount === 1 ? "family" : "families"} failed.`);
        } else {
          // Mixed results: some successful, some failed, possibly some skipped
          let subtitle = `${successfulCount} out of ${totalProcessed} ${totalProcessed === 1 ? "family" : "families"} inserted`;
          if (skippedCount > 0) {
            subtitle += `. ${skippedCount} ${skippedCount === 1 ? "family" : "families"} already exist`;
          }
          if (failedCount > 0) {
            subtitle += `. ${failedCount} ${failedCount === 1 ? "family" : "families"} failed`;
          }
          subtitle += ".";
          setBulkImportProgressTitle("Bulk Import Partially Successful");
          setBulkImportProgressSubtitle(subtitle);
        }

        await fetchFamilies(pageNumber, searchTerm);
      } else {
        throw new Error(
          response.response?.data?.message || "Failed to import families"
        );
      }
    } catch (error) {
      // Update progress snackbar with error
      setBulkImportProgressTitle("Bulk Import Failed");
      setBulkImportProgressSubtitle(error.response?.data?.message || error.message || "An error occurred during bulk import");
      setFailedFamiliesFile(null);
    } finally {
      setLoading(false);
      // Clear sessionStorage when import completes
      setTimeout(() => {
        sessionStorage.removeItem('bulkImportProgress');
      }, 15000); // Clear after snackbar auto-closes
    }
  };

  const handleDownloadFailedFamilies = () => {
    if (!failedFamiliesFile) {
      setShowSnackbar(true);
      setSnackbarTitle("Download Failed");
      setSnackbarSubtitle("Error report is not available.");
      setSnackbarType("error");
      return;
    }

    const success = downloadFailedFamiliesFile(failedFamiliesFile, (error) => {
      setShowSnackbar(true);
      setSnackbarTitle("Download Failed");
      setSnackbarSubtitle(error.message || "Failed to download error report. Please try again.");
      setSnackbarType("error");
    });

    if (success) {
      // Optionally show success message
      // setShowSnackbar(true);
      // setSnackbarTitle("Download Started");
      // setSnackbarSubtitle("Failed families report is downloading...");
      // setSnackbarType("success");
    }
  };

  const handleUpdateEmailStatus = async (csvFile) => {
    setLoading(true);
    try {
      const response = await APIUpdateEmailStatus(
        csvFile,
        setLoading,
        institutionId,
        token
      );

      if (response.status === 200) {
        const data = response.data?.data || {};
        const totalInCsv = data.totalInCsv || 0;
        const updated = data.updated || 0;
        const notFound = data.notFound || 0;
        const notFoundEmails = data.notFoundEmails || [];

        // Refresh the family data to get updated email status
        await fetchFamilies(pageNumber, searchTerm);

        // Show success message
        setShowSnackbar(true);
        if (updated > 0) {
          setSnackbarTitle("Emails Sent Successfully");
          setSnackbarSubtitle(
            `${updated} out of ${totalInCsv} ${totalInCsv === 1 ? "email" : "emails"} sent successfully.` +
            (notFound > 0 ? ` ${notFound} ${notFound === 1 ? "email was" : "emails were"} already sent or not found.` : "")
          );
          setSnackbarType("success");
        } else {
          setSnackbarTitle("No Emails Sent");
          setSnackbarSubtitle(
            `No emails were sent. All ${notFound} ${notFound === 1 ? "email was" : "emails were"} already sent previously or not found in the system.`
          );
          setSnackbarType("error");
        }

        // Return result data for the modal to display
        return {
          totalInCsv,
          updated,
          notFound,
          notFoundEmails: notFoundEmails.length > 0 ? notFoundEmails : undefined,
        };
      } else {
        throw new Error(
          response.response?.data?.message || "Failed to update email status"
        );
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle("Failed to Send Emails");
      setSnackbarSubtitle(error.response?.data?.message || error.message || "An error occurred while trying to send emails. Please try again.");
      setSnackbarType("error");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handlePopoverAction = (action, rowId) => {
    const selectedRow = fetchedFamilyData?.families.find(
      (row) => row.familyId === rowId
    );

    if (!selectedRow) return;
    const updatedParents = selectedRow.parent.map((parent) => ({
      ...parent,
      status:
        parent.isActive === undefined
          ? "active"
          : parent.isActive
          ? "active"
          : "inactive",
    }));

    const updatedRow = {
      ...selectedRow,
      parent: updatedParents,
    };
    setSelectedRowData(updatedRow);
    if (action === "edit") {
      handleOpenEdit(updatedRow);
    } else if (action === "delete") {
      setShowDeleteModal(true);
    } else {
      handleOpenEdit(updatedRow);
      setOnlyView(true);
    }
  };

  const handleOpenEdit = (selectedRow) => {
    const subscription = selectedRow.subscription || {};
    let validUntilPicker = "";
    if (subscription.validUntil) {
      try {
        const instant = parseISO(subscription.validUntil);
        if (!Number.isNaN(instant.getTime())) {
          validUntilPicker = formatInTimeZone(instant, timezone, "yyyy-MM-dd");
        }
      } catch {
        validUntilPicker = "";
      }
    }

    const formattedInput = {
      address: selectedRow.parent[0]?.address || "",
      subscription: {
        plan: normalizeSubscriptionPlanFromRow(subscription, selectedRow),
        validUntil: validUntilPicker,
        gracePeriodDays:
          subscription.gracePeriodDays === undefined ||
          subscription.gracePeriodDays === null
            ? "7"
            : String(subscription.gracePeriodDays),
        startAt: subscription.startAt || "",
      },
      parent: selectedRow.parent.map((parent, index) => ({
        id: parent._id || index + 1,
        firstName: parent.firstName || "",
        lastName: parent.lastName || "",
        preferredName: parent.preferredName || "",
        email: parent.email || "",
        phoneNumber: formatCombineFamilyPhone({
          callingCode: parent.callingCode || "",
          phoneNumber: parent.phoneNumber || "",
        }),
        firebaseUid: parent.firebaseUid || "",
        status: parent.status || "",
        emailOnboardStatus: parent.emailOnboardStatus || 'sent',
      })),
      child: selectedRow.children.map((child, index) => ({
        id: child._id || index + 1,
        stops: child.associatedStops || {},
        firstName: child.firstName || "",
        lastName: child.lastName || "",
        preferredName: child.preferredName || "",
        alightPermission: child.alightPermission || false,
        class: child.class,
        comment: child.comment,
        dob: formatDate(child.dob) || "",
        grade: child.grade || "",
        childImg: child.childImg || "",
        note: child.note || "",
        preferences: child.preferences || "",
      })),
      familyId: selectedRow.familyId || "",
    };

    setGracePeriodError(false);
    setInput(formattedInput);
    setInitialEditInput(formattedInput);
    setAction("edit");
  };

  const getChanges = (initialArray, newArray, compareKey) => {
    const created = newArray.filter(
      (newItem) =>
        !initialArray.some(
          (initItem) => initItem[compareKey] === newItem[compareKey]
        )
    );

    const deleted = initialArray.filter(
      (initItem) =>
        !newArray.some(
          (newItem) => newItem[compareKey] === initItem[compareKey]
        )
    );

    const edited = newArray
      .map((newItem) => {
        const original = initialArray.find(
          (initItem) => initItem[compareKey] === newItem[compareKey]
        );
        if (!original) return null;

        const isDifferent =
          JSON.stringify(original) !== JSON.stringify(newItem);
        if (!isDifferent) return null;

        return {
          ...newItem,
          firebaseUid: original.firebaseUid || "",
          id: original.id || "",
        };
      })
      .filter(Boolean);

    return { created, edited, deleted };
  };

  const buildSubscriptionForPayload = () => {
    const toValidUntilEndOfDayIso = (yyyyMmDd) => {
      if (!yyyyMmDd) return "";
      try {
        return fromZonedTime(`${yyyyMmDd}T23:59:59`, timezone).toISOString();
      } catch {
        return "";
      }
    };
    const isRestrictedPlan = input.subscription?.plan === "RESTRICTED";
    const restrictedTodayYyyyMmDd = formatInTimeZone(
      new Date(),
      timezone,
      "yyyy-MM-dd"
    );
    const startAt =
      action === "edit" && input.subscription?.startAt
        ? input.subscription.startAt
        : new Date().toISOString();

    return {
      plan: input.subscription?.plan || "",
      validUntil: isRestrictedPlan
        ? toValidUntilEndOfDayIso(restrictedTodayYyyyMmDd)
        : toValidUntilEndOfDayIso(input.subscription?.validUntil),
      startAt,
      gracePeriodDays: isRestrictedPlan
        ? 7
        : Number(input.subscription?.gracePeriodDays || 0),
    };
  };

  const handleEdit = async () => {
    setLoading(true);

    try {
      const {
        created: createdParents,
        edited: editedParents,
        deleted: deletedParents,
      } = getChanges(initialEditInput.parent, input.parent, "email");
      const {
        created: createdChildren,
        edited: editedChildren,
        deleted: deletedChildren,
      } = getChanges(initialEditInput.child, input.child, "id");

      const addressChanged = initialEditInput.address !== input.address;
      if (addressChanged) {
        initialEditInput.parent.forEach((parent) => {
          if (
            !editedParents.some((p) => p.id === parent.id) &&
            !deletedParents.some((p) => p.id === parent.id)
          ) {
            editedParents.push({
              ...parent,
              firebaseUid: parent.firebaseUid || "",
            });
          }
        });
      }

      const filterEmpty = (obj) =>
        Object.fromEntries(
          Object.entries(obj).filter(
            ([_, value]) =>
              value !== undefined &&
              !(Array.isArray(value) && value.length === 0)
          )
        );

      const hasChanged = (oldValue, newValue) =>
        JSON.stringify(oldValue) !== JSON.stringify(newValue);

      const familyData = {
        institutionId,
        familyId: input.familyId || "",
        parents: filterEmpty({
          create: createdParents.map((p) => {
            const { callingCode, phoneNumber } = formatParseFamilyPhone(
              p.phoneNumber || ""
            );
            return {
              firebaseUid: p.firebaseUid || "",
              firstName: p.firstName || "",
              lastName: p.lastName || "",
              preferredName: p.preferredName || "",
              email: p.email || "",
              callingCode,
              phoneNumber,
              address: input.address || "",
              institutionId,
            };
          }),
          update: editedParents.map((p) => {
            const { callingCode, phoneNumber } = formatParseFamilyPhone(
              p.phoneNumber || ""
            );
            return {
              firebaseUid: p.firebaseUid || "",
              firstName: p.firstName || "",
              lastName: p.lastName || "",
              preferredName: p.preferredName || "",
              email: p.email || "",
              callingCode,
              phoneNumber,
              address: input.address || "",
              institutionId,
            };
          }),
          delete: deletedParents.map((p) => {
            const { callingCode, phoneNumber } = formatParseFamilyPhone(
              p.phoneNumber || ""
            );
            return {
              firebaseUid: p.firebaseUid || "",
              firstName: p.firstName || "",
              lastName: p.lastName || "",
              preferredName: p.preferredName || "",
              email: p.email || "",
              callingCode,
              phoneNumber,
              address: p.address || "",
              institutionId,
            };
          }),
        }),
        childs: filterEmpty({
          create: createdChildren.map((c) => ({
            firstName: c.firstName || "",
            lastName: c.lastName || "",
            preferredName: c.preferredName || "",
            alightPermission: c.alightPermission || false,
            childImg: c.childImg || null,
            class: c.class || "",
            dob: c.dob || "",
            grade: c.grade || "",
            note: c.note || "",
            institutionId,
            preferences: c.preferences || "",
            ...(c.alightPermission ? { comment: c.comment || "" } : {}),
          })),
          update: editedChildren.map((c) => {
            const originalChild =
              initialEditInput.child.find((child) => child.id === c.id) || {};
            return filterEmpty({
              childId: c._id || c.id || "",
              firstName: c.firstName || "",
              lastName: c.lastName || "",
              preferredName: c.preferredName || "",
              alightPermission: c.alightPermission,
              class: c.class || "",
              dob: c.dob || "",
              grade: c.grade || "",
              note: c.note || "",
              preferences: c.preferences || "",
              institutionId,
              ...(hasChanged(originalChild.childImg, c.childImg)
                ? { childImg: c.childImg || null }
                : {}),
              ...(c.alightPermission ? { comment: c.comment || "" } : {}),
            });
          }),
          delete: deletedChildren.map((c) => ({
            childId: c._id || c.id || "",
            firstName: c.firstName || "",
            lastName: c.lastName || "",
            preferredName: c.preferredName || "",
            alightPermission: c.alightPermission || false,
            childImg: c.childImg || null,
            class: c.class || "",
            dob: c.dob || "",
            grade: c.grade || "",
            note: c.note || "",
            preferences: c.preferences || "",
            institutionId,
            ...(c.alightPermission ? { comment: c.comment || "" } : {}),
          })),
        }),
      };

      const response = await APIUpdateFamily(
        familyData,
        setLoading,
        institutionId,
        token
      );

      if (response.status === 200) {
        if (ENABLE_FAMILY_ACCOUNT_TYPES) {
          if (!input.familyId) {
            throw new Error("Missing family ID for subscription update");
          }
          const subscriptionResponse = await APIUpdateFamilySubscription(
            input.familyId,
            { subscription: buildSubscriptionForPayload() },
            setLoading,
            institutionId,
            token
          );
          if (subscriptionResponse.status !== 200) {
            throw new Error(
              subscriptionResponse.response?.data?.message ||
                "Failed to update family subscription"
            );
          }
        }

        const statusChangedParents = input.parent.filter((p) => {
          const original = initialEditInput.parent.find(
            (ip) => ip.email === p.email
          );
          return original && original.status !== p.status;
        });
        if (statusChangedParents.length > 0) {
          setLoading(true);
          await Promise.all(
            statusChangedParents.map(async (parent) => {
              try {
                const newStatus =
                  parent.status === "active" ? "activated" : "deactivated";
                await APIUpdateParentStatus(
                  parent.id,
                  newStatus,
                  token,
                  institutionId
                );
              } catch (error) {
                console.error(
                  `Failed to update status for parent ${parent.email}:`,
                  error
                );
              }
            })
          );
          setLoading(false);
        }
        setShowSnackbar(true);
        setSnackbarTitle("Family Updated");
        setSnackbarSubtitle("Family has been successfully updated");
        setSnackbarType("success");
        await fetchFamilies();
        handleReset();
      } else {
        throw new Error(
          response.response?.data?.message || "Failed to update family"
        );
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle("Update Failed");
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    const emptyState = {
      address: "",
      subscription: {
        plan: "",
        validUntil: "",
        gracePeriodDays: "7",
        startAt: "",
      },
      parent: [],
      child: [],
    };

    setInput(emptyState);
    setInitialEditInput(emptyState);
    setAction("create");
  };

  const handleCreate = async () => {
    setLoading(true);

    try {
      const familyData = {
        institutionId,
        familyId: "",
        ...(ENABLE_FAMILY_ACCOUNT_TYPES
          ? { subscription: buildSubscriptionForPayload() }
          : {}),
        parents: {
          create: input.parent.map((p) => {
            const { callingCode, phoneNumber } = formatParseFamilyPhone(
              p.phoneNumber || ""
            );
            return {
              firstName: p.firstName || "",
              lastName: p.lastName || "",
              preferredName: p.preferredName || "",
              email: p.email || "",
              callingCode,
              phoneNumber,
              address: input.address || "",
              institutionId,
            };
          }),
        },
        childs: {
          create: input.child.map((c) => ({
            firstName: c.firstName || "",
            lastName: c.lastName || "",
            preferredName: c.preferredName || "",
            alightPermission: c.alightPermission || false,
            childImg: c.childImg || null,
            class: c.class || "",
            dob: c.dob || "",
            grade: c.grade || "",
            note: c.note || "",
            preferences: c.preferences || "",
            institutionId,
            ...(c.alightPermission ? { comment: c.comment || "" } : {}),
          })),
        },
      };

      const response = await APIUpdateFamily(
        familyData,
        setLoading,
        institutionId,
        token
      );

      if (response.status === 200) {
        setShowSnackbar(true);
        setSnackbarTitle("Family Created");
        setSnackbarSubtitle("Family has been successfully created");
        setSnackbarType("success");
        // Only call status API if parent is inactive
        const inactiveParents = input.parent.filter(
          (p) => p.status === "inactive"
        );

        if (inactiveParents.length > 0) {
          await Promise.all(
            inactiveParents.map(async (parent) => {
              try {
                await APIUpdateParentStatus(
                  parent.id,
                  "deactivated",
                  token,
                  institutionId
                );
              } catch (error) {
                console.error(
                  `Failed to deactivate parent ${parent.email}:`,
                  error
                );
              }
            })
          );
        }
      } else {
        throw new Error(
          response.response?.data?.message || "Failed to create family"
        );
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle("Create Failed");
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      await fetchFamilies();
      setLoading(false);
      handleReset();
    }
  };

  const handleDelete = async (row) => {
    setLoading(true);

    const familyId = row.familyId;

    try {
      const response = await APIDeleteFamily(
        familyId,
        setLoading,
        institutionId,
        token
      );

      if (response?.status === 200) {
        setShowSnackbar(true);
        setSnackbarTitle("Delete Successful");
        setSnackbarSubtitle("Family has been deleted successfully");
        setSnackbarType("success");
      } else {
        throw new Error(
          response?.response?.data?.message || "Failed to delete family"
        );
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle("Delete Failed");
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      await fetchFamilies();
      setShowDeleteModal(false);
      handleReset();
      setLoading(false);
    }
  };

  const handleAddModalParent = () => {
    setAddModalType("parent");
    setShowAddModal(true);
  };

  const handleAddModalChild = () => {
    setAddModalType("child");
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setAddModalType("");
    setSelectedChildData(null);
    setSelectedParentData(null);
  };

  const handleAddParent = (parentData) => {
    const newParentData = {
      ...parentData,
      id: parentId,
      status: "active",
    };

    setInput((prevInput) => ({
      ...prevInput,
      parent: [...prevInput.parent, newParentData],
    }));

    setParentId((prevId) => prevId + 1);
  };

  const handleAddChild = (childData) => {
    const newChildData = {
      ...childData,
      id: childId,
    };

    setInput((prevInput) => ({
      ...prevInput,
      child: [...prevInput.child, newChildData],
    }));

    setChildId((prevId) => prevId + 1);
  };

  const handleUpdateParent = (updatedData) => {
    // Find the parent by id and update it
    const updatedParentList = input.parent.map((parent) => {
      if (parent.id === updatedData.id) {
        // If found, update the parent data with the new values
        return { ...parent, ...updatedData };
      }
      return parent;
    });

    setInput((prevState) => ({
      ...prevState,
      parent: updatedParentList,
    }));
    setSelectedParentData(null);
  };

  const handleUpdateChild = (updatedData) => {
    // Find the child by id and update it
    const updatedChildList = input.child.map((child) => {
      if (child.id === updatedData.id) {
        // If found, update the child data with the new values
        return { ...child, ...updatedData };
      }
      return child;
    });

    setInput((prevState) => ({
      ...prevState,
      child: updatedChildList,
    }));
    setSelectedChildData(null);
  };

  const handleParentPopoverAction = (action, rowId) => {
    if (action === "edit") {
      const parentToEdit = input.parent.find((parent) => parent.id === rowId);
      setSelectedParentData(parentToEdit);
      handleAddModalParent();
    } else if (action === "delete") {
      const updatedParents = input.parent.filter(
        (parent) => parent.id !== rowId
      );
      setInput((prevState) => ({
        ...prevState,
        parent: updatedParents,
      }));
    } else if (action === "view") {
      const parentToView = input.parent.find((parent) => parent.id === rowId);
      setSelectedParentData(parentToView);
      handleAddModalParent();
      setOnlyView(true);
    } else if (action === "activate" || action === "deactivate") {
      const updatedParents = input.parent.map((parent) =>
        parent.id === rowId
          ? { ...parent, status: action === "activate" ? "active" : "inactive" }
          : parent
      );

      setInput((prevState) => ({
        ...prevState,
        parent: updatedParents,
      }));
    }
  };

  const handleChildPopoverAction = (action, rowId) => {
    if (action === "edit") {
      const childToEdit = input.child.find((child) => child.id === rowId);
      setSelectedChildData(childToEdit);
      handleAddModalChild();
    } else if (action === "delete") {
      const updatedChildren = input.child.filter((child) => child.id !== rowId);
      setInput((prevState) => ({
        ...prevState,
        child: updatedChildren,
      }));
    }
    if (action === "view") {
      const childToEdit = input.child.find((child) => child.id === rowId);
      setSelectedChildData(childToEdit);
      handleAddModalChild();
      setOnlyView(true);
    }
  };

  const validateForm = () => {
    // Check if there's at least one parent and one child
    const isParentAndChildPresent =
      input.parent.length > 0 && input.child.length > 0;

    // Check if all necessary fields are populated
    const isBaseFormPopulated =
      input.address && input.parent.length > 0 && input.child.length > 0;
    const plan = input.subscription?.plan;
    const isRestrictedPlan = plan === "RESTRICTED";
    const isSubscriptionPopulated =
      plan &&
      (isRestrictedPlan ||
        (input.subscription?.validUntil &&
          input.subscription?.gracePeriodDays !== ""));
    const trialMaxDate = getTrialMaxDate(timezone);
    const isTrialDateValid =
      input.subscription?.plan !== "TRIAL" ||
      (input.subscription?.validUntil &&
        input.subscription.validUntil <= trialMaxDate);
    const isGracePeriodValid =
      isRestrictedPlan ||
      /^\d+$/.test(String(input.subscription?.gracePeriodDays || ""));
    const subscriptionEditable =
      ENABLE_FAMILY_ACCOUNT_TYPES &&
      (action === "create" || action === "edit") &&
      !onlyView;
    const areFieldsPopulated = subscriptionEditable
      ? isBaseFormPopulated &&
        isSubscriptionPopulated &&
        isGracePeriodValid &&
        isTrialDateValid
      : isBaseFormPopulated;

    // Check if any changes have been made
    const formStateForCompare = (state) => {
      if (!state) return state;
      if (ENABLE_FAMILY_ACCOUNT_TYPES) return state;
      const { subscription: _subscription, ...rest } = state;
      return rest;
    };
    const isChanged =
      JSON.stringify(formStateForCompare(input)) !==
      JSON.stringify(formStateForCompare(initialEditInput));

    return isParentAndChildPresent && areFieldsPopulated && isChanged;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "plan") {
      if (value === "TRIAL") {
        const trialMaxDate = getTrialMaxDate(timezone);
        setGracePeriodError(false);
        setInput((prevInput) => ({
          ...prevInput,
          subscription: {
            ...prevInput.subscription,
            plan: value,
            validUntil:
              prevInput.subscription?.validUntil &&
              prevInput.subscription.validUntil > trialMaxDate
                ? trialMaxDate
                : prevInput.subscription?.validUntil || "",
            gracePeriodDays: "7",
          },
        }));
        return;
      }
      if (value === "RESTRICTED") {
        setGracePeriodError(false);
        setInput((prevInput) => ({
          ...prevInput,
          subscription: {
            ...prevInput.subscription,
            plan: value,
          },
        }));
        return;
      }
    }

    if (name === "gracePeriodDays") {
      setGracePeriodError(value !== "" && !/^\d+$/.test(value));
    }

    if (["plan", "validUntil", "gracePeriodDays"].includes(name)) {
      setInput((prevInput) => ({
        ...prevInput,
        subscription: {
          ...prevInput.subscription,
          [name]: value,
        },
      }));
      return;
    }

    setInput((prevInput) => ({
      ...prevInput,
      [name]: value,
    }));
  };

  const renderSubscriptionSection = (readOnly) => {
    if (!ENABLE_FAMILY_ACCOUNT_TYPES) return null;

    return (
      <div className="name-form-container">
        <div className="input-label">
          <RequiredLabel>Subscription</RequiredLabel>
          <div className="input-description">
            Configure the family subscription details.
          </div>
          <div className="input-description">Plan: Select subscription tier.</div>
          {input.subscription?.plan !== "RESTRICTED" && (
            <>
              <div className="input-description">
                Valid Until: Subscription end date.
              </div>
              <div className="input-description">
                Grace Period (days): Extra days after expiry.
              </div>
            </>
          )}
        </div>
        <div
          className="input-container"
          style={{
            flex: 1,
            marginRight: "1rem",
            display: "grid",
            gridTemplateColumns:
              input.subscription?.plan === "RESTRICTED"
                ? `${subscriptionFieldWidth}`
                : `${subscriptionFieldWidth} ${subscriptionFieldWidth} ${subscriptionFieldWidth}`,
            gap: "1rem",
            alignItems: "start",
          }}
        >
          <div style={{ width: subscriptionFieldWidth }}>
            <Dropdown
              label="Plan"
              options={subscriptionPlanOptions}
              name="plan"
              placeholder="Select plan"
              onChangeFunction={handleInputChange}
              input={input.subscription || {}}
              width="100%"
              disabled={readOnly}
            />
          </div>
          {input.subscription?.plan !== "RESTRICTED" && (
            <>
              <div style={{ width: subscriptionFieldWidth }}>
                <DatePicker
                  label="Valid Until"
                  placeholder="Select date"
                  name="validUntil"
                  disablePast={true}
                  shouldDisableDate={(date) => disableToday(date, timezone)}
                  disable={readOnly}
                  error={false}
                  setError={() => {}}
                  input={input.subscription || {}}
                  setInput={(updater) =>
                    setInput((prev) => ({
                      ...prev,
                      subscription:
                        typeof updater === "function"
                          ? updater(prev.subscription || {})
                          : updater,
                    }))
                  }
                  maxDate={
                    input.subscription?.plan === "TRIAL"
                      ? getTrialMaxDate(timezone)
                      : undefined
                  }
                  errorMessage=""
                  width="100%"
                />
              </div>
              <div style={{ width: subscriptionFieldWidth }}>
                <TextInput
                  label="Grace Period (days)"
                  placeholder="Enter days"
                  type="text"
                  disable={
                    readOnly || input.subscription?.plan === "TRIAL"
                  }
                  error={gracePeriodError}
                  errorMessage="Enter numbers only"
                  setError={setGracePeriodError}
                  input={input.subscription || {}}
                  setInput={setInput}
                  name="gracePeriodDays"
                  onChangeFunction={handleInputChange}
                  keyUp={keyUp}
                  setKeyUp={setKeyUp}
                  width="100%"
                />
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderTitle = () => {
    switch (action) {
      case "edit":
        return (
          <>
            <div className="family-title-container">
              <div className="family-title">
                {onlyView ? (
                  <>
                    <Title
                      title="View Family"
                      subtitle="View an existing family in BusBuddy"
                      titleSize="1.5rem"
                      subtitleSize="1rem"
                      gap="0.5rem"
                      lineHeight="2rem"
                    />
                  </>
                ) : (
                  <>
                    <Title
                      title="Edit Family"
                      subtitle="Edit an existing family in BusBuddy"
                      titleSize="1.5rem"
                      subtitleSize="1rem"
                      gap="0.5rem"
                      lineHeight="2rem"
                    />
                  </>
                )}
              </div>
            </div>
          </>
        );
      case "create":
        return (
          <>
            <Title
              title="Create Family"
              subtitle="Add a new family to BusBuddy and add parents or children"
              titleSize="1.5rem"
              subtitleSize="1rem"
              gap="0.5rem"
              lineHeight="2rem"
            />
          </>
        );
      default:
        return (
          <Title
            title="Family Management"
            subtitle="Manage the families which exist in the Bus Buddy platform"
            titleSize="1.5rem"
            subtitleSize="1rem"
            gap="0.5rem"
            lineHeight="2rem"
          />
        );
    }
  };

  const renderContent = () => {
    switch (action) {
      case "edit":
        return (
          <>
            <div className="name-form-container">
              <div className="input-label">
                <RequiredLabel>Parent Information</RequiredLabel>
                <div className="input-description">
                  This table contains the parents that will be associated with
                  this family.
                </div>
              </div>
              <div
                className="input-container"
                style={{ flex: 1, paddingBottom: "1rem", marginRight: "1rem" }}
              >
                <Table
                  data={input.parent}
                  searchText="Search by first name..."
                  headers={parentHeaders}
                  showEmptyHeader={true}
                  showFilter={false}
                  showSearchBar={false}
                  module="parent"
                  onClick={handleAddModalParent}
                  handlePopoverAction={handleParentPopoverAction}
                  showTableAction={
                    (role === "ADMIN" ||
                      role === "SUPERADMIN" ||
                      role === "EDITOR") &&
                    !onlyView
                  }
                  height="25rem"
                  emptyStateSize="small"
                />
              </div>
            </div>
            <div className="name-form-container">
              <div className="input-label">
                <RequiredLabel>Child Information</RequiredLabel>
                <div className="input-description">
                  This table contains the children that will be associated with
                  this family.
                </div>
              </div>
              <div
                className="input-container"
                style={{ flex: 1, paddingBottom: "1rem", marginRight: "1rem" }}
              >
                <Table
                  data={input.child}
                  searchText="Search by name..."
                  headers={childHeaders}
                  showEmptyHeader={true}
                  showFilter={false}
                  showSearchBar={false}
                  module="child"
                  onClick={handleAddModalChild}
                  handlePopoverAction={handleChildPopoverAction}
                  showTableAction={
                    (role === "ADMIN" ||
                      role === "SUPERADMIN" ||
                      role === "EDITOR") &&
                    !onlyView
                  }
                  height="25rem"
                  emptyStateSize="small"
                />
              </div>
            </div>
            <div className="name-form-container">
              <div className="input-label">
                <RequiredLabel>Family Address</RequiredLabel>
                <div className="input-description">
                  This address will be associated with the family.
                </div>
              </div>
              <div
                className="input-container"
                style={{ flex: 1, marginRight: "1rem" }}
              >
                <TextInput
                  disable={onlyView}
                  placeholder={onlyView ? input.address : "Enter address here"}
                  type="textarea"
                  input={onlyView ? "" : input}
                  setInput={setInput}
                  name="address"
                  onChangeFunction={handleInputChange}
                  setError={setError}
                  keyUp={keyUp}
                  setKeyUp={setKeyUp}
                />
              </div>
            </div>
            {renderSubscriptionSection(onlyView)}
            {onlyView ? (
              <div className="button-container-edit">
                <SecondaryButton
                  width="8rem"
                  label="Cancel"
                  imageIcon={false}
                  onClickFunction={handleReset}
                />
              </div>
            ) : (
              <div className="button-container-edit">
                <SecondaryButton
                  width="6rem"
                  label="Cancel"
                  imageIcon={false}
                  onClickFunction={handleReset}
                />
                <PrimaryButton
                  disable={!validateForm()}
                  width="6rem"
                  label="Save"
                  onClickFunction={handleEdit}
                  loading={loading}
                />
              </div>
            )}
            <FamilyModal
              isOpen={showAddModal}
              onClose={handleCloseAddModal}
              type={addModalType}
              handleAddParent={handleAddParent}
              handleAddChild={handleAddChild}
              handleUpdateParent={handleUpdateParent}
              handleUpdateChild={handleUpdateChild}
              parentId={parentId}
              childId={childId}
              selectedParentData={selectedParentData}
              selectedChildData={selectedChildData}
              setSelectedChildData={setSelectedChildData}
              setSelectedParentData={setSelectedParentData}
              onlyView={onlyView}
            />
          </>
        );
      case "create":
        return (
          <>
            <div className="name-form-container">
              <div className="input-label">
                <RequiredLabel>Parent Information</RequiredLabel>
                <div className="input-description">
                  This table contains the parents that will be associated with
                  this family.
                </div>
              </div>
              <div
                className="input-container"
                style={{ flex: 1, paddingBottom: "1rem", marginRight: "1rem" }}
              >
                <Table
                  data={input.parent}
                  searchText="Search by first name..."
                  headers={parentHeaders}
                  showEmptyHeader={true}
                  showFilter={false}
                  showSearchBar={false}
                  module="parent"
                  onClick={handleAddModalParent}
                  handlePopoverAction={handleParentPopoverAction}
                  showTableAction={
                    (role === "ADMIN" ||
                      role === "SUPERADMIN" ||
                      role === "EDITOR") &&
                    !onlyView
                  }
                  height="25rem"
                  emptyStateSize="small"
                />
              </div>
            </div>
            <div className="name-form-container">
              <div className="input-label">
                <RequiredLabel>Child Information</RequiredLabel>
                <div className="input-description">
                  This table contains the children that will be associated with
                  this family.
                </div>
              </div>
              <div
                className="input-container"
                style={{ flex: 1, paddingBottom: "1rem", marginRight: "1rem" }}
              >
                <Table
                  data={input.child}
                  searchText="Search by name..."
                  headers={childHeaders}
                  showEmptyHeader={true}
                  showFilter={false}
                  showSearchBar={false}
                  module="child"
                  onClick={handleAddModalChild}
                  handlePopoverAction={handleChildPopoverAction}
                  showTableAction={
                    (role === "ADMIN" ||
                      role === "SUPERADMIN" ||
                      role === "EDITOR") &&
                    !onlyView
                  }
                  height="25rem"
                  emptyStateSize="small"
                />
              </div>
            </div>
            <div className="name-form-container">
              <div className="input-label">
                <RequiredLabel>Family Address</RequiredLabel>
                <div className="input-description">
                  This address will be associated with the family.
                </div>
              </div>
              <div
                className="input-container"
                style={{ flex: 1, marginRight: "1rem" }}
              >
                <TextInput
                  placeholder="Enter address here"
                  type="textarea"
                  input={input}
                  setInput={setInput}
                  name="address"
                  onChangeFunction={handleInputChange}
                  setError={setError}
                  keyUp={keyUp}
                  setKeyUp={setKeyUp}
                />
              </div>
            </div>
            {renderSubscriptionSection(false)}
            <div className="button-container-edit">
              <SecondaryButton
                width="6rem"
                label="Cancel"
                imageIcon={false}
                onClickFunction={handleReset}
              />
              <PrimaryButton
                disable={!validateForm()}
                width="6rem"
                label="Create"
                onClickFunction={handleCreate}
                loading={loading}
              />
            </div>
            <FamilyModal
              isOpen={showAddModal}
              onClose={handleCloseAddModal}
              type={addModalType}
              handleAddParent={handleAddParent}
              handleAddChild={handleAddChild}
              handleUpdateParent={handleUpdateParent}
              handleUpdateChild={handleUpdateChild}
              parentId={parentId}
              childId={childId}
              selectedParentData={selectedParentData}
              selectedChildData={selectedChildData}
              setSelectedChildData={setSelectedChildData}
              setSelectedParentData={setSelectedParentData}
              onlyView={onlyView ? true : false}
            />
          </>
        );
      default:
        return (
          <Table
            data={fetchedFamilyData?.families || []}
            searchText="Search by parent, child, or stop name..."
            headers={headers}
            showEmptyHeader={true}
            showFilter={false}
            showSearchBar={true}
            handlePopoverAction={handlePopoverAction}
            showTableAction={
              (role === "ADMIN" ||
                role === "SUPERADMIN" ||
                role === "EDITOR") &&
              !onlyView
            }
            onClick={handleOpenCreate}
            module="family"
            loading={loading}
            height="75vh"
            totalRecords={fetchedFamilyData?.pagination?.total || 0}
            useBackendPagination={true}
            onPageChange={fetchFamilies}
            totalPages={fetchedFamilyData?.pagination?.totalPages || 1}
            currentPage={pageNumber}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onBulkImport={() => setShowBulkImportModal(true)}
            onUpdateEmailStatus={() => setShowUpdateEmailStatusModal(true)}
          />
        );
    }
  };

  return (
    <div
      className={`main-page family-management-page ${
        masterAdminMode ? "master-admin-embedded-page" : ""
      } ${!action ? "family-management-table-view" : ""}`}
    >
      {!masterAdminMode && (
        <NavBar
          type="familyManagement"
          action={action}
          handleReset={handleReset}
        />
      )}
      <div
        className={`content-area family-management-content-area ${
          masterAdminMode ? "master-admin-embedded-content" : ""
        }`}
      >
        {!masterAdminMode && (
          <div className="title-container mobile-hide-page-title">
            {renderTitle()}
          </div>
        )}
        <div className="main-content-container family-management-main-content-container">
          {renderContent()}
        </div>
      </div>
      <DeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={handleDelete}
        itemName={selectedRowData}
        module="parent"
        loading={loading}
      />
      <BulkImportModal
        isOpen={showBulkImportModal}
        onClose={() => setShowBulkImportModal(false)}
        onImport={handleBulkImport}
        loading={loading}
      />
      <UpdateEmailStatusModal
        isOpen={showUpdateEmailStatusModal}
        onClose={() => setShowUpdateEmailStatusModal(false)}
        onUpdate={handleUpdateEmailStatus}
        loading={loading}
      />
      {showSnackbar && (
        <div className="notification">
          <SnackBar
            state={showSnackbar}
            setState={setShowSnackbar}
            title={snackbarTitle}
            subtitle={snackbarSubtitle}
            module={snackbarType}
            action={snackbarType}
          />
        </div>
      )}
      {showBulkImportProgress && (
        <div className="notification">
          <SnackBar
            state={showBulkImportProgress}
            setState={(value) => {
              setShowBulkImportProgress(value);
              if (!value) {
                // Clear sessionStorage when snackbar is closed
                sessionStorage.removeItem('bulkImportProgress');
                // Clear failed families file when snackbar is closed
                setFailedFamiliesFile(null);
              }
            }}
            title={bulkImportProgressTitle}
            subtitle={bulkImportProgressSubtitle}
            module={loading ? "liveUpdate" : bulkImportProgressTitle.includes("Failed") ? "error" : (bulkImportProgressTitle.includes("Successful") || bulkImportProgressTitle.includes("Completed")) ? "success" : "error"}
            action={loading ? "liveUpdate" : bulkImportProgressTitle.includes("Failed") ? "error" : (bulkImportProgressTitle.includes("Successful") || bulkImportProgressTitle.includes("Completed")) ? "success" : "error"}
            disableAutoClose={true}
            showProgress={loading}
            textButton={!loading && failedFamiliesFile ? true : false}
            textButtonLabel={!loading && failedFamiliesFile ? "Download Failed Families Report" : ""}
            textButtonFunction={handleDownloadFailedFamilies}
          />
        </div>
      )}
    </div>
  );
}
